/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useState, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { t } from 'i18next';

// Component imports
import { Divider, Grid, Tabs, Tooltip } from 'antd';
import { SmileOutlined } from '@ant-design/icons';
import { LiveChatIcon } from '../../Icons/LiveChatIcon';
import { StoredVariablesIcon } from '../../Icons/StoredVariablesIcon';
import { TicketsIcon } from '../../Icons/TicketsIcon';
import { ContactIcon } from '../../Icons/ContactIcon';
import { SessionDetailsIcon } from '../../Icons/SessionDetailsIcon';
import SessionDetailsTab from './SessionDetailsTab';
import SessionVariablesTab from './SessionVariablesTab';
import SessionCSATTab from './SessionCSATTab';
import SessionLiveChatTab from './SessionLiveChatTab';
import SessionContactTab from './SessionContactTab';
import SessionTicketsTab from './SessionTicketsTab';
import { isEmptyObject } from '../../../utils/is/isEmptyObject';
import SessionInteractionLogTab from './SessionInteractionLogTab';
import SessionInteractionVariablesTab from './SessionInteractionVariablesTab';

// Misc imports
import { IS_HRWIZ_SESSION_CONTACTS_DISABLED, IS_HRWIZ_SESSION_TICKETS_DISABLED } from '../../../constants/env';

// Constant declarations
const { useBreakpoint } = Grid;

const CHAT_SESSION_INTERACTION_METADATA_TABS = {
  INTERACTION_LOG: {
    label: t('Interaction Log'),
    key: 'interactionLog',
    icon: 'Interaction Log',
    priority: 1,
    isVisible: true,
    children: (props) => (
      <Fragment>
        <SessionInteractionLogTab selectedChatSessionMessage={props.selectedChatSessionMessage} />
      </Fragment>
    )
  },
  INTERACTION_VARIABLES: {
    label: t('Variables Values'),
    key: 'variables',
    icon: 'Variable Values',
    priority: 2,
    isVisible: true,
    children: (props) => (
      <Fragment>
        <SessionInteractionVariablesTab selectedChatSessionMessage={props.selectedChatSessionMessage} />
      </Fragment>
    )
  }
};

const CHAT_SESSION_TABS = {
  DETAILS: {
    label: t('Session Details'),
    key: 'details',
    icon: <SessionDetailsIcon className="fs-xl m-0" />,
    priority: 1,
    isVisible: ({ chatSession }) => !!chatSession,
    children: (props) => (
      <SessionDetailsTab
        chatSession={props.chatSession}
        tags={props.tags}
        onFetchChatSession={props.onFetchChatSession}
        isLoading={props.isFetchingChatSession || props.isFetchingTags}
        activeBotDeployment={props.activeBotDeployment}
      />
    )
  },
  VARIABLES: {
    label: t('Variables'),
    key: 'variables',
    icon: <StoredVariablesIcon className="fs-xl m-0" />,
    priority: 2,
    isVisible: ({ chatSession }) => chatSession?.variables?.length > 0,
    children: (props) => <SessionVariablesTab chatSession={props.chatSession} />
  },
  CSAT: {
    label: t('CSAT'),
    key: 'csat',
    icon: <SmileOutlined className="fs-xl m-0" />,
    priority: 3,
    isVisible: ({ chatSession }) => chatSession?.csatSurveys?.filter(survey => survey?.status === 'ANSWERED').length > 0,
    children: (props) => <SessionCSATTab chatSession={props.chatSession} />
  },
  LIVE_CHAT: {
    label: t('LiveChat'),
    key: 'livechat',
    icon: <LiveChatIcon className="fs-xl m-0" />,
    priority: 4,
    isVisible: ({ chatSession }) => chatSession.liveChats?.length > 0,
    children: (props) => <SessionLiveChatTab chatSession={props.chatSession} />
  },
  CONTACT: {
    label: t('Contact'),
    key: 'contact',
    icon: <ContactIcon className="fs-xl m-0" />,
    priority: 5,
    isVisible: ({ chatSession }) => !IS_HRWIZ_SESSION_CONTACTS_DISABLED && (chatSession.metadata?.subscriberName || chatSession.metadata?.subscriberEmail ||chatSession.metadata?.subscriberCustomData),
    children: (props) => <SessionContactTab chatSession={props.chatSession} />
  },
  TICKETS: {
    label: t('Tickets'),
    key: 'tickets',
    icon: <TicketsIcon className="fs-xl m-0" />,
    priority: 6,
    isVisible: () => !IS_HRWIZ_SESSION_TICKETS_DISABLED,
    children: (props) => <SessionTicketsTab chatSession={props.chatSession} />
  }
};

const ChatSessionDetails = ({
  chatSession,
  isFetchingChatSession,
  onFetchChatSession,
  tags,
  isFetchingTags,
  activeBotDeployment,
  selectedChatSessionMessage
}) => {
  const screens = useBreakpoint();
  const isMobileOrTabletSize = (screens.xs || screens.sm || screens.md || screens.lg) && !screens.xl;

  const isInteractionView = !isEmptyObject(selectedChatSessionMessage);
  const [activeTabKey, setActiveTabKey] = useState(isInteractionView ? CHAT_SESSION_INTERACTION_METADATA_TABS.INTERACTION_LOG.key : CHAT_SESSION_TABS.DETAILS.key);

  const tabItems = useMemo(() => Object.values(isInteractionView ? CHAT_SESSION_INTERACTION_METADATA_TABS : CHAT_SESSION_TABS).filter((tab) => {
    // If no 'isVisible' function is defined, default is true
    if (typeof tab.isVisible === 'function') {
      return tab.isVisible({ chatSession });
    }
    return true;
  })
    .sort((a, b) => a.priority - b.priority)
    .map((tab) => ({
      key: tab.key,
      label: (
        <Tooltip title={tab.label}>
          <span className="d-flex align-items-center">
            {tab.icon}
          </span>
        </Tooltip>
      ),
      children: !isEmptyObject(selectedChatSessionMessage)
        ? tab.children({
          selectedChatSessionMessage
        })
        : tab.children({
          chatSession,
          tags,
          isFetchingChatSession,
          onFetchChatSession,
          isFetchingTags,
          activeBotDeployment
        })
    })), [isInteractionView, chatSession, tags, isFetchingChatSession, onFetchChatSession, isFetchingTags, activeBotDeployment, selectedChatSessionMessage]);

  useEffect(() => {
    if (isInteractionView) {
      setActiveTabKey(CHAT_SESSION_INTERACTION_METADATA_TABS.INTERACTION_LOG.key);
    } else {
      setActiveTabKey(CHAT_SESSION_TABS.DETAILS.key);
    }
  }, [isInteractionView]);

  // If the currently active tab was filtered out (e.g., no data), reset it to the first visible tab
  useEffect(() => {
    const isActiveTabVisible = tabItems.find((item) => item.key === activeTabKey);
    if (!isActiveTabVisible && tabItems.length > 0) {
      setActiveTabKey(tabItems[0].key);
    }
  }, [tabItems, activeTabKey]);

  return (
    <Tabs
      destroyInactiveTabPane
      activeKey={activeTabKey}
      onChange={setActiveTabKey}
      items={tabItems}
      tabBarGutter={24}
      renderTabBar={(props, DefaultTabBar) => (
        <Fragment>
          <DefaultTabBar {...props} className="mb-0 pl-2" />
          <Divider className="mt-0" />
        </Fragment>
      )}
      className={classNames('scrollbar-custom mt-1 p-1', { 'mb-6': isMobileOrTabletSize })}
    />
  );
};

ChatSessionDetails.propTypes = {
  chatSession: PropTypes.object,
  isFetchingChatSession: PropTypes.bool,
  onFetchChatSession: PropTypes.func,
  tags: PropTypes.array,
  isFetchingTags: PropTypes.bool,
  activeBotDeployment: PropTypes.object,
  selectedChatSessionMessage: PropTypes.object
};

export default ChatSessionDetails;
